import { useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

/** Fraction of the remaining distance closed each frame. */
const EASE = 0.075;
/** Below this the eye cannot tell, so the loop parks itself. */
const SETTLE_EPSILON = 0.0005;
/** Only devices with a real pointer; a finger has nothing to hover with. */
const FINE_POINTER = '(pointer: fine)';

/**
 * Publishes the pointer's position over the element as the custom properties
 * `--pointer-x` and `--pointer-y`, each eased into the range -1..1 measured
 * from the element's centre.
 *
 * Custom properties rather than inline transforms, so every layer can choose
 * its own depth in CSS. They are consumed through the standalone `translate`
 * property, which composes with — rather than overwrites — the `transform`
 * the layers' keyframes already animate.
 *
 * The easing runs on its own rAF loop that stops once the values settle, so an
 * idle page costs nothing. Pointer events only ever set a target; they never
 * touch the DOM, which keeps a flood of `pointermove` off the layout path.
 */
export const usePointerParallax = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const element = ref.current;
    if (!element || prefersReducedMotion) return;
    if (!window.matchMedia(FINE_POINTER).matches) return;

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let frame = 0;

    const step = () => {
      currentX += (targetX - currentX) * EASE;
      currentY += (targetY - currentY) * EASE;

      element.style.setProperty('--pointer-x', currentX.toFixed(4));
      element.style.setProperty('--pointer-y', currentY.toFixed(4));

      if (
        Math.abs(targetX - currentX) < SETTLE_EPSILON &&
        Math.abs(targetY - currentY) < SETTLE_EPSILON
      ) {
        frame = 0;
        return;
      }

      frame = requestAnimationFrame(step);
    };

    const start = () => {
      if (!frame) frame = requestAnimationFrame(step);
    };

    const onPointerMove = (event: PointerEvent) => {
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      // Clamped so a pointer dragged beyond the element cannot fling the
      // layers past their intended travel.
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = ((event.clientY - rect.top) / rect.height) * 2 - 1;

      targetX = Math.max(-1, Math.min(1, x));
      targetY = Math.max(-1, Math.min(1, y));
      start();
    };

    /** Leaving the window recentres the sky instead of freezing it mid-tilt. */
    const onRecentre = () => {
      targetX = 0;
      targetY = 0;
      start();
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('blur', onRecentre);
    document.addEventListener('pointerleave', onRecentre);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('blur', onRecentre);
      document.removeEventListener('pointerleave', onRecentre);
      element.style.removeProperty('--pointer-x');
      element.style.removeProperty('--pointer-y');
    };
  }, [prefersReducedMotion]);

  return ref;
};
